import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import styled from "styled-components";

import Button from "../Button";
import Modal, { ModalTitle } from "../Modal";

function SyndicateModal({ open, onOpenChange, onSubmit }) {
  const [initials, setInitials] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setInitials("");
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const valid = /^[A-Z]{2}$/.test(initials);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await onSubmit({ initials });
    if (result?.ok) return;
    setSubmitting(false);
    setError(result?.error || "Submission failed. Try again.");
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} width={500} topOnMobile>
      <ModalTitle>
        Hello <Cap>Founder</Cap>
      </ModalTitle>
      <Body>
        <p>
          Initial your artwork. Senior partners will review your piece and
          potentially list it in the gallery.
        </p>
        <Form onSubmit={handleSubmit}>
          <Label htmlFor="initials">Your initials</Label>
          <Input
            id="initials"
            value={initials}
            onChange={(e) =>
              setInitials(
                e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z]/g, "")
                  .slice(0, 2),
              )
            }
            maxLength={2}
            placeholder="MA"
            autoFocus={
              typeof window !== "undefined" &&
              !window.matchMedia("(pointer: coarse)").matches
            }
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
            disabled={submitting}
          />
          {error && <ErrorText>{error}</ErrorText>}
          <Footer>
            <Dialog.Close asChild>
              <Button
                type="button"
                variant="default"
                size="medium"
                disabled={submitting}
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="submit"
              variant="primary"
              size="medium"
              disabled={!valid || submitting}
            >
              {submitting ? "Syndicating…" : "Syndicate"}
            </Button>
          </Footer>
        </Form>
      </Body>
    </Modal>
  );
}

const Cap = styled.span`
  color: var(--color-orange-700);
`;

const Body = styled.div`
  font-size: 1rem;
  line-height: 1.55;
  color: var(--color-text);

  p {
    margin: 0 0 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
`;

const Input = styled.input`
  font-family: "Fraunces", serif;
  font-size: 2.5rem;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  width: 100%;
  padding: 0.5rem;
  background: var(--color-orange-50, #fff8f1);
  border: 2px solid var(--color-orange-300);
  border-radius: 6px;
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-orange-700);
  }
`;

const ErrorText = styled.div`
  font-size: 0.875rem;
  color: var(--color-alert, #b00020);
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

export default SyndicateModal;
